import React from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../constants';
import { fetchKioskSession, kioskUserFoundAction, setKioskMode } from '../../utils/kiosk';
import '../../css/kiosk.css';

// Fast enough that a driver isn't left looking at a QR they've already scanned, slow enough not to
// hammer the device. The pairing itself is pushed to the Pi over the hub, so this poll is only ever
// waiting on a local read.
const POLL_INTERVAL_MS = 2000;

/**
 * The truck console's landing screen.
 *
 * Displays the pairing QR until a driver approves it on their phone, then puts the minted kiosk
 * token into the store and hands over to the normal driver views. This is the only screen in the app
 * that runs without a signed-in user, which is why it sits outside the Main layout.
 */
export class KioskPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            session: null,
            error: null,
        };
        this.poll = this.poll.bind(this);
    }

    componentDidMount() {
        // Reaching this screen at all means this browser is a console. Remember it, so the app comes
        // back signed in after the idle timer or AutoReload reloads the page.
        setKioskMode(true);
        this.poll();
        this.timer = setInterval(this.poll, POLL_INTERVAL_MS);
    }

    componentWillUnmount() {
        this.unmounted = true;
        clearInterval(this.timer);
    }

    poll() {
        return fetchKioskSession().then(session => {
            if (this.unmounted) {
                return;
            }

            if (session.unreachable) {
                this.setState({
                    loading: false,
                    session: null,
                    error: 'Waiting for the truck system to start up.',
                });
                return;
            }

            if (session.paired && session.token) {
                clearInterval(this.timer);
                this.props.onPaired(session);
                return;
            }

            // Keyed off what came back rather than the status code. A console with a code to show
            // is working, however the device chose to dress that up; only a device that couldn't
            // reach RunPlanner has something to explain. The SPA is served from the cloud and the
            // truck-proxy container ships on its own Balena release, so the two are routinely a
            // version apart and the status has already changed once (401 -> 200).
            this.setState({
                loading: false,
                session,
                error: session.userCode ? null : (session.message || null),
            });
        });
    }

    renderCode(code) {
        // Split into two groups of four: far easier to read off a screen at arm's length, and to
        // type by hand when the QR won't scan off wet or greasy glass.
        const grouped = code.length === 8 ? `${code.slice(0, 4)} ${code.slice(4)}` : code;
        return <div className="kiosk-code">{grouped}</div>;
    }

    render() {
        const { loading, session, error } = this.state;

        if (loading) {
            return (
                <div className="kiosk-shell">
                    <div className="kiosk-panel"><h1 className="kiosk-title">Starting up…</h1></div>
                </div>
            );
        }

        if (error || !session || !session.userCode) {
            return (
                <div className="kiosk-shell">
                    <div className="kiosk-panel">
                        <h1 className="kiosk-title">Can&apos;t sign in yet</h1>
                        <p className="kiosk-lead">
                            {error || 'Waiting for a sign-in code.'}
                        </p>
                        <p className="kiosk-hint">
                            Signing in needs a connection. If the truck is underground or out of range,
                            move somewhere with signal and this screen will retry on its own.
                        </p>
                        {session && session.truckNumberPlate ? (
                            <div className="kiosk-plate">{session.truckNumberPlate}</div>
                        ) : null}
                    </div>
                </div>
            );
        }

        return (
            <div className="kiosk-shell">
                <div className="kiosk-panel">
                    <h1 className="kiosk-title">Scan to sign in</h1>
                    <p className="kiosk-lead">
                        Open RunPlanner on your phone and scan this code.
                    </p>

                    <img
                        className="kiosk-qr"
                        src={`${BASE_URL}/api/kiosk/pairing/${encodeURIComponent(session.userCode)}/qr`}
                        alt={`Sign-in code ${session.userCode}`}
                    />

                    <p className="kiosk-hint">Or enter this code in RunPlanner:</p>
                    {this.renderCode(session.userCode)}

                    <div className="kiosk-plate">{session.truckNumberPlate}</div>
                </div>
            </div>
        );
    }
}

// Functional wrapper so the class component can use the router's navigate hook.
function KioskPageContainer(props) {
    const navigate = useNavigate();

    const onPaired = session => {
        props.dispatch(kioskUserFoundAction(session));
        navigate('/runs/driver', { replace: true });
    };

    return <KioskPage {...props} onPaired={onPaired} />;
}

export default connect()(KioskPageContainer);
